import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { bookCategories, featuredBooks, supplierBookStats } from "@/mocks/books";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownhongyunmall.com";

const booksJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "教育书籍专区 | 皇冠鸿运商城",
    "description": "高考备考、研究生考试、会计考试、英语考试、感恩教育、幸福开心、儿童成长、节日礼物书，共32本精选教育书籍。",
    "url": `${SITE_URL}/books`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "教育书籍专区", "item": `${SITE_URL}/books` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "教育书籍精选",
    "description": "高考·研究生·会计·英语考试·感恩教育·幸福开心·儿童成长·节日礼物书",
    "url": `${SITE_URL}/books`,
    "numberOfItems": 32
  }
];

type TabType = "browse" | "supplier";

export default function BooksPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("browse");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [newBook, setNewBook] = useState({
    title: "",
    subtitle: "",
    author: "",
    publisher: "",
    price: "",
    originalPrice: "",
    category: "gaokao",
    description: "",
    commissionRate: "10",
    tags: "",
  });

  const filteredBooks = featuredBooks.filter((book) => {
    const matchCat = selectedCategory === "all" || book.category === selectedCategory;
    const matchSearch =
      !searchQuery ||
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.description.includes(searchQuery) ||
      book.categoryLabel.includes(searchQuery);
    return matchCat && matchSearch;
  });

  const sortedBooks = [...filteredBooks].sort((a, b) => {
    if (sortBy === "popular") return b.sold - a.sold;
    if (sortBy === "rating") return b.rating - a.rating;
    if (sortBy === "price_asc") return a.price - b.price;
    if (sortBy === "price_desc") return b.price - a.price;
    return 0;
  });

  const handleAddBook = (e: React.FormEvent) => {
    e.preventDefault();
    setShowAddModal(false);
    setAddSuccess(true);
    setNewBook({
      title: "", subtitle: "", author: "", publisher: "",
      price: "", originalPrice: "", category: "gaokao",
      description: "", commissionRate: "10", tags: "",
    });
    setTimeout(() => setAddSuccess(false), 3000);
  };

  return (
    <div className="min-h-screen bg-[#fdf8f3]" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <SeoHead
        title="教育书籍专区 | 高考·研究生·会计·英语·感恩教育 | 皇冠鸿运商城"
        description="皇冠鸿运商城教育书籍专区，精选32本高考备考、研究生考试、会计考试、英语考试、感恩教育、幸福开心、儿童成长、节日礼物书，让知识改变命运，让爱心温暖世界。"
        keywords="高考备考书,研究生考试书,会计考试书,英语考试书,感恩教育书,儿童成长书,节日礼物书"
        canonical="/books"
        jsonLd={booksJsonLd}
      />

      {/* Header */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #c0392b 0%, #8e1a0e 40%, #5d1008 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 30px, rgba(255,255,255,0.05) 30px, rgba(255,255,255,0.05) 60px)"
            }}
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-red-200 hover:text-white text-sm mb-6 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </button>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-400 rounded-xl">
                  <i className="ri-book-open-line text-red-800 text-xl"></i>
                </div>
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  教育书籍专区
                </h1>
              </div>
              <p className="text-red-200 text-sm leading-relaxed max-w-xl">
                高考 · 研究生 · 会计 · 英语考试 · 感恩教育 · 幸福成长 · 节日礼物书<br />
                让知识改变命运，让爱心温暖世界，让每一本书都成为人生的礼物
              </p>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "在售书籍", value: supplierBookStats.totalBooks.toLocaleString(), icon: "ri-book-line" },
                { label: "合作出版商", value: supplierBookStats.totalSuppliers, icon: "ri-building-line" },
                { label: "累计销量", value: "234万+", icon: "ri-shopping-bag-line" },
                { label: "平均评分", value: supplierBookStats.avgRating, icon: "ri-star-fill" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                  <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1">
                    <i className={`${stat.icon} text-yellow-300 text-lg`}></i>
                  </div>
                  <div className="text-white font-bold text-lg">{stat.value}</div>
                  <div className="text-red-200 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mt-6">
            <button
              onClick={() => setActiveTab("browse")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === "browse"
                  ? "bg-yellow-400 text-red-900 font-bold"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <i className="ri-search-line mr-1.5"></i>浏览书籍
            </button>
            <button
              onClick={() => setActiveTab("supplier")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === "supplier"
                  ? "bg-yellow-400 text-red-900 font-bold"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <i className="ri-store-line mr-1.5"></i>供应商入驻 / 上架书籍
            </button>
          </div>
        </div>
      </div>

      {/* Success toast */}
      {addSuccess && (
        <div className="fixed top-6 right-6 z-50 bg-green-500 text-white px-6 py-3 rounded-xl flex items-center gap-2 animate-pulse">
          <i className="ri-checkbox-circle-line text-lg"></i>
          书籍上架申请已提交，审核通过后即可展示！
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* BROWSE TAB */}
        {activeTab === "browse" && (
          <div>
            {/* Category pills */}
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === "all"
                    ? "bg-red-600 text-white"
                    : "bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-200"
                }`}
              >
                全部书籍
              </button>
              {bookCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                    selectedCategory === cat.id
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-200"
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.label}
                  <span className="text-xs opacity-70">({cat.count})</span>
                </button>
              ))}
            </div>

            {/* Search + Sort bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
              <div className="flex-1 flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
                <i className="ri-search-line text-gray-400 ml-3"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索书名、作者、出版社..."
                  className="flex-1 px-3 py-2.5 text-sm outline-none bg-transparent"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white outline-none cursor-pointer"
              >
                <option value="popular">最受欢迎</option>
                <option value="rating">评分最高</option>
                <option value="price_asc">价格从低到高</option>
                <option value="price_desc">价格从高到低</option>
              </select>
              <span className="text-sm text-gray-500 whitespace-nowrap self-center">
                共 {sortedBooks.length} 本书籍
              </span>
            </div>

            {/* Category highlight cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
              {bookCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`${cat.color} rounded-xl p-3 text-center cursor-pointer transition-all hover:scale-105 border-2 ${
                    selectedCategory === cat.id ? "border-current" : "border-transparent"
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div className="text-xs font-medium leading-tight">{cat.label}</div>
                </button>
              ))}
            </div>

            {/* Books grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {sortedBooks.map((book) => (
                <div
                  key={book.id}
                  className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-red-200 transition-all cursor-pointer group"
                >
                  <div className="relative overflow-hidden" style={{ height: "200px" }}>
                    <img
                      src={book.image}
                      alt={book.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        {book.categoryLabel}
                      </span>
                    </div>
                    {book.commissionRate >= 15 && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-yellow-400 text-red-900 text-xs px-2 py-0.5 rounded-full font-bold">
                          佣金{book.commissionRate}%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-0.5 line-clamp-2">
                      {book.title}
                    </h3>
                    <p className="text-gray-400 text-xs mb-2 line-clamp-1">{book.subtitle}</p>
                    <p className="text-gray-500 text-xs">{book.author}</p>
                    <div className="flex items-center gap-1 mb-2">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                      <span className="text-xs font-medium text-gray-700">{book.rating}</span>
                      <span className="text-xs text-gray-400">({book.reviews.toLocaleString()})</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {book.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="bg-red-50 text-red-600 text-xs px-1.5 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-red-600 font-bold text-base">¥{book.price}</span>
                        <span className="text-gray-400 text-xs line-through ml-1">¥{book.originalPrice}</span>
                      </div>
                      <span className="text-gray-400 text-xs">{book.sold.toLocaleString()}已售</span>
                    </div>
                    <button className="w-full mt-2 bg-red-500 hover:bg-red-600 text-white text-xs py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-medium">
                      立即购买
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {sortedBooks.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-book-open-line text-5xl text-gray-200"></i>
                </div>
                <p>暂无符合条件的书籍</p>
              </div>
            )}

            {/* Gratitude & Education Banner */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 rounded-2xl overflow-hidden relative" style={{ minHeight: "180px" }}>
                <img
                  src="https://public.readdy.ai/ai/img_res/2f5e1d9be8b4962e39dc5571842f538b.jpg"
                  alt="感恩教育"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                <div className="relative z-10 p-6 flex flex-col justify-center h-full">
                  <div className="text-yellow-300 text-sm font-medium mb-1">感恩教育 · 爱心培养</div>
                  <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                    感恩父母 · 关爱长者 · 爱护小朋友
                  </h3>
                  <p className="text-gray-200 text-sm mb-3">让每一本书都传递爱与温暖，培养孩子感恩之心</p>
                  <button
                    onClick={() => setSelectedCategory("gratitude")}
                    className="bg-yellow-400 hover:bg-yellow-500 text-red-900 px-4 py-2 rounded-full text-sm font-bold w-fit cursor-pointer transition-colors whitespace-nowrap"
                  >
                    浏览感恩教育书籍
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="bg-gradient-to-br from-red-500 to-red-700 rounded-2xl p-5 text-white flex-1">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-3">
                    <i className="ri-graduation-cap-line text-xl"></i>
                  </div>
                  <h4 className="font-bold text-base mb-1">高考 · 考研冲刺</h4>
                  <p className="text-red-100 text-xs mb-3">精选备考书籍，助力梦想成真</p>
                  <button
                    onClick={() => setSelectedCategory("gaokao")}
                    className="bg-white text-red-600 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
                  >
                    查看备考书籍
                  </button>
                </div>
                <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-5 text-white flex-1">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-3">
                    <i className="ri-gift-line text-xl"></i>
                  </div>
                  <h4 className="font-bold text-base mb-1">节日礼物书</h4>
                  <p className="text-amber-100 text-xs mb-3">精美礼盒装，送礼首选</p>
                  <button
                    onClick={() => setSelectedCategory("gift")}
                    className="bg-white text-amber-600 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-amber-50 transition-colors"
                  >
                    选购礼物书
                  </button>
                </div>
              </div>
            </div>

            {/* Values section */}
            <div className="mt-8 bg-white rounded-2xl p-6 border border-gray-100">
              <h3 className="text-center text-lg font-bold text-gray-900 mb-6" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                教育的力量 · 书籍的价值
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: "ri-heart-line", color: "text-pink-500 bg-pink-50", title: "感恩父母", desc: "培养孩子感恩之心，珍惜父母的爱与付出" },
                  { icon: "ri-user-heart-line", color: "text-red-500 bg-red-50", title: "关爱长者", desc: "尊老敬老，传承中华传统美德" },
                  { icon: "ri-seedling-line", color: "text-green-500 bg-green-50", title: "爱护小朋友", desc: "关爱儿童成长，给孩子最好的教育礼物" },
                  { icon: "ri-global-line", color: "text-amber-500 bg-amber-50", title: "回馈社会", desc: "感恩社会，积极参与公益，共建美好家园" },
                ].map((item) => (
                  <div key={item.title} className="text-center p-4 rounded-xl bg-gray-50">
                    <div className={`w-12 h-12 flex items-center justify-center ${item.color} rounded-full mx-auto mb-3`}>
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SUPPLIER TAB */}
        {activeTab === "supplier" && (
          <div>
            {/* Supplier intro */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                {
                  icon: "ri-store-line",
                  color: "bg-red-50 text-red-600",
                  title: "入驻申请",
                  desc: "出版社、书商、教育机构均可申请入驻，经资质核验后即可上架书籍",
                  action: "申请入驻",
                },
                {
                  icon: "ri-book-2-line",
                  color: "bg-amber-50 text-amber-600",
                  title: "上架书籍",
                  desc: "填写书名、简介、价格、分类等信息，审核通过后即可在平台展示销售",
                  action: "立即上架",
                },
                {
                  icon: "ri-vip-crown-line",
                  color: "bg-green-50 text-green-600",
                  title: "达人推广",
                  desc: "设置佣金比例，吸引抖音、TikTok、微信等平台达人帮助推广，扩大销量",
                  action: "了解佣金",
                },
              ].map((card) => (
                <div key={card.title} className="bg-white rounded-xl p-5 border border-gray-100">
                  <div className={`w-12 h-12 flex items-center justify-center ${card.color} rounded-xl mb-3`}>
                    <i className={`${card.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{card.desc}</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                  >
                    {card.action}
                  </button>
                </div>
              ))}
            </div>

            {/* Add book button */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">我的书籍列表</h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line"></i> 上架新书籍
              </button>
            </div>

            {/* Supplier book list */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium">书籍信息</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium hidden md:table-cell">分类</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium">价格</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium hidden md:table-cell">销量</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium hidden lg:table-cell">评分</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium hidden lg:table-cell">佣金</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {featuredBooks.slice(0, 8).map((book, idx) => (
                    <tr key={book.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? "" : "bg-gray-50/30"}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden">
                            <img src={book.image} alt={book.title} className="w-full h-full object-cover object-top" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm line-clamp-1">{book.title}</div>
                            <div className="text-gray-400 text-xs">{book.author}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="bg-red-50 text-red-600 text-xs px-2 py-1 rounded-full">{book.categoryLabel}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-red-600 font-bold">¥{book.price}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-600">{book.sold.toLocaleString()}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          <span className="text-gray-700 text-xs">{book.rating}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="bg-yellow-50 text-yellow-700 text-xs px-2 py-1 rounded-full font-medium">{book.commissionRate}%</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer px-2 py-1 rounded hover:bg-blue-50 transition-colors whitespace-nowrap">编辑</button>
                          <button className="text-xs text-red-500 hover:text-red-700 cursor-pointer px-2 py-1 rounded hover:bg-red-50 transition-colors whitespace-nowrap">下架</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Commission tips */}
            <div className="mt-6 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-5 border border-amber-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400 rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-amber-900 mb-1">佣金设置建议</h4>
                  <p className="text-amber-700 text-sm leading-relaxed">
                    建议感恩教育类书籍设置 <strong>15%-20%</strong> 佣金，吸引更多达人推广；
                    高考/考研类书籍设置 <strong>10%-15%</strong>；节日礼物书可设置 <strong>18%-25%</strong>。
                    佣金越高，达人推广积极性越强，销量提升越明显！
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Book Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-lg">上架新书籍</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleAddBook} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">书名 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={newBook.title}
                    onChange={(e) => setNewBook({ ...newBook, title: e.target.value })}
                    placeholder="请输入书名"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">副标题</label>
                  <input
                    type="text"
                    value={newBook.subtitle}
                    onChange={(e) => setNewBook({ ...newBook, subtitle: e.target.value })}
                    placeholder="请输入副标题（可选）"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">作者 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={newBook.author}
                    onChange={(e) => setNewBook({ ...newBook, author: e.target.value })}
                    placeholder="请输入作者姓名"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">出版社 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={newBook.publisher}
                    onChange={(e) => setNewBook({ ...newBook, publisher: e.target.value })}
                    placeholder="请输入出版社名称"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">售价（元）<span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={newBook.price}
                    onChange={(e) => setNewBook({ ...newBook, price: e.target.value })}
                    placeholder="如：68"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">原价（元）</label>
                  <input
                    type="number"
                    min="1"
                    value={newBook.originalPrice}
                    onChange={(e) => setNewBook({ ...newBook, originalPrice: e.target.value })}
                    placeholder="如：98（可选）"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">书籍分类 <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={newBook.category}
                    onChange={(e) => setNewBook({ ...newBook, category: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors bg-white cursor-pointer"
                  >
                    {bookCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    达人佣金比例 <span className="text-red-500">*</span>
                    <span className="text-gray-400 font-normal ml-1">（建议10%-25%）</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="5"
                      max="30"
                      value={newBook.commissionRate}
                      onChange={(e) => setNewBook({ ...newBook, commissionRate: e.target.value })}
                      className="flex-1 cursor-pointer accent-red-500"
                    />
                    <span className="text-red-600 font-bold text-sm w-12 text-right">{newBook.commissionRate}%</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">书籍简介 <span className="text-red-500">*</span></label>
                <textarea
                  required
                  value={newBook.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setNewBook({ ...newBook, description: e.target.value });
                  }}
                  placeholder="请详细描述书籍内容、特色、适合人群等，帮助读者了解这本书的价值..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors resize-none"
                />
                <div className="text-right text-xs text-gray-400 mt-1">{newBook.description.length}/500</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">标签（用逗号分隔）</label>
                <input
                  type="text"
                  value={newBook.tags}
                  onChange={(e) => setNewBook({ ...newBook, tags: e.target.value })}
                  placeholder="如：高考必备, 真题解析, 满分技巧"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-colors whitespace-nowrap"
                >
                  提交上架申请
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
